import React, { useState, useEffect } from 'react';
import {
  RectangleGroupIcon,
  TagIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  HomeModernIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../api/api';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import { Button } from '../../components/ui/Button';

const MarketingBudgetDefinitions = () => {
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [maingroups, setMaingroups] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedParentFilter, setSelectedParentFilter] = useState('');

  // Tanım kartları
  const definitionCards = [
    {
      id: 'maingroups',
      title: 'Ana Gruplar',
      icon: RectangleGroupIcon,
      color: 'bg-blue-500',
      endpoint: '/marketing-budget/maingroups/',
      fields: [
        { name: 'maingroup_name', label: 'Ana Grup Adı', type: 'text', required: true }
      ]
    },
    {
      id: 'subgroups',
      title: 'Alt Gruplar',
      icon: TagIcon,
      color: 'bg-green-500',
      endpoint: '/marketing-budget/subgroups/',
      fields: [
        { name: 'subgroup_name', label: 'Alt Grup Adı', type: 'text', required: true },
        { name: 'maingroup', label: 'Ana Grup', type: 'select', required: true, options: 'maingroups' }
      ]
    },
    {
      id: 'markets',
      title: 'Pazarlar',
      icon: GlobeAltIcon,
      color: 'bg-purple-500',
      endpoint: '/marketing-budget/markets/',
      fields: [
        { name: 'budget_market_name', label: 'Pazar Adı', type: 'text', required: true }
      ]
    },
    {
      id: 'types',
      title: 'Bütçe Tipleri',
      icon: DocumentTextIcon,
      color: 'bg-orange-500',
      endpoint: '/marketing-budget/types/',
      fields: [
        { name: 'budget_type', label: 'Bütçe Tipi', type: 'text', required: true }
      ]
    },
    {
      id: 'countries',
      title: 'Ülkeler',
      icon: MapPinIcon,
      color: 'bg-red-500',
      endpoint: '/marketing-budget/countries/',
      fields: [
        { name: 'country_name', label: 'Ülke Adı', type: 'text', required: true },
        { name: 'market', label: 'Pazar', type: 'select', required: true, options: 'markets' }
      ]
    },
    {
      id: 'cities',
      title: 'Şehirler',
      icon: BuildingOfficeIcon,
      color: 'bg-yellow-500',
      endpoint: '/marketing-budget/cities/',
      fields: [
        { name: 'city_name', label: 'Şehir Adı', type: 'text', required: true },
        { name: 'country', label: 'Ülke', type: 'select', required: true, options: 'countries' }
      ]
    },
    {
      id: 'agencies',
      title: 'Acenteler',
      icon: BuildingStorefrontIcon,
      color: 'bg-pink-500',
      endpoint: '/marketing-budget/agencies/',
      fields: [
        { name: 'agency_name', label: 'Acente Adı', type: 'text', required: true }
      ]
    },
    {
      id: 'hotels',
      title: 'Oteller',
      icon: HomeModernIcon,
      color: 'bg-indigo-500',
      endpoint: '/marketing-budget/hotels/',
      fields: [
        { name: 'hotel_name', label: 'Otel Adı', type: 'text', required: true }
      ]
    }
  ];

  useEffect(() => {
    if (selectedDefinition) {
      fetchData();
    }
    // Gerekli listeleri yükle
    fetchAllRequiredLists();
  }, [selectedDefinition]);

  // Filtre değiştiğinde verileri filtrele
  useEffect(() => {
    if (!selectedParentFilter) {
      setFilteredData(data);
      return;
    }

    let filterField = '';
    if (selectedDefinition?.id === 'subgroups') filterField = 'maingroup';
    if (selectedDefinition?.id === 'countries') filterField = 'market';
    if (selectedDefinition?.id === 'cities') filterField = 'country';

    if (filterField) {
      const filtered = data.filter(item => String(item[filterField]) === String(selectedParentFilter));
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [selectedParentFilter, data, selectedDefinition]);

  const fetchAllRequiredLists = async () => {
    try {
      const [mg, m, c] = await Promise.all([
        apiRequest('/marketing-budget/maingroups/'),
        apiRequest('/marketing-budget/markets/'),
        apiRequest('/marketing-budget/countries/')
      ]);
      setMaingroups(mg.results || mg || []);
      setMarkets(m.results || m || []);
      setCountries(c.results || c || []);
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchData = async () => {
    if (!selectedDefinition) return;

    setLoading(true);
    try {
      const response = await apiRequest(`${selectedDefinition.endpoint}?page_size=1000`);
      setData(response.results || response || []);
    } catch (error) {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (card) => {
    setSelectedDefinition(card);
    setEditingItem(null);
    setFormData({});
    setSelectedParentFilter(''); // Filtreyi sıfırla
  };

  const handleBack = () => {
    setSelectedDefinition(null);
    setData([]);
    setSelectedParentFilter(''); // Filtreyi sıfırla
  };

  const handleNew = () => {
    setEditingItem(null);
    setFormData({});
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setIsModalOpen(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) return;

    try {
      await apiRequest(`${selectedDefinition.endpoint}${item.id}/`, {
        method: 'DELETE'
      });
      fetchData();
    } catch (error) {
      alert('Kayıt silinemedi. Bu kayıt başka yerlerde kullanılıyor olabilir.');
    }
  };

  const handleSave = async () => {
    try {
      // Validasyon
      const requiredFields = selectedDefinition.fields.filter(f => f.required);
      for (const field of requiredFields) {
        if (!formData[field.name]) {
          alert(`${field.label} alanı zorunludur.`);
          return;
        }
      }

      if (editingItem) {
        // Güncelleme
        await apiRequest(`${selectedDefinition.endpoint}${editingItem.id}/`, {
          method: 'PATCH',
          body: JSON.stringify(formData)
        });
      } else {
        // Yeni kayıt
        await apiRequest(selectedDefinition.endpoint, {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      }

      setIsModalOpen(false);
      setFormData({});
      setEditingItem(null);
      fetchData();
    } catch (error) {
      alert('Kayıt başarısız oldu.');
    }
  };

  const renderField = (field) => {
    if (field.type === 'select') {
      let options = [];
      let labelKey = '';

      if (field.options === 'maingroups') {
        options = maingroups;
        labelKey = 'maingroup_name';
      } else if (field.options === 'markets') {
        options = markets;
        labelKey = 'budget_market_name';
      } else if (field.options === 'countries') {
        options = countries;
        labelKey = 'country_name';
      }

      return (
        <select
          value={formData[field.name] || ''}
          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          required={field.required}
        >
          <option value="">Seçiniz</option>
          {options.map(option => (
            <option key={option.id} value={option.id}>
              {option[labelKey] || option.name}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type}
        value={formData[field.name] || ''}
        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        required={field.required}
      />
    );
  };

  const getDisplayValue = (item, field) => {
    if (field.name === 'maingroup') return item.maingroup_name || '-';
    if (field.name === 'market' && selectedDefinition.id === 'countries') return item.market_name || '-';
    if (field.name === 'country' && selectedDefinition.id === 'cities') return item.country_name || '-';
    return item[field.name] || '-';
  };

  if (!selectedDefinition) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Marketing Budget Tanımlar
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Bütçe yönetimi için kullanılan tanımları düzenleyin
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {definitionCards.map((card) => {
              const Icon = card.icon;
              return (
                <button
                  key={card.id}
                  onClick={() => handleCardClick(card)}
                  className="group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 text-left overflow-hidden"
                >
                  <div className={`absolute inset-0 ${card.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                  <div className={`inline-flex p-3 rounded-lg ${card.color} bg-opacity-10 mb-4`}>
                    <Icon className={`h-8 w-8 ${card.color.replace('bg-', 'text-')}`} />
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {card.title}
                  </h3>

                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {card.title} tanımlarını yönetin
                  </p>

                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const Icon = selectedDefinition.icon;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900 pt-6 pb-4 px-6">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <svg className="h-6 w-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className={`p-3 rounded-lg ${selectedDefinition.color} bg-opacity-10`}>
                <Icon className={`h-8 w-8 ${selectedDefinition.color.replace('bg-', 'text-')}`} />
              </div>

              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedDefinition.title}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedDefinition.title} tanımlarını yönetin
                </p>
              </div>
            </div>

          </div>

          <ActionToolbar className="mb-4">
            <IconActionButton
              icon={PlusIcon}
              onClick={handleNew}
              title="Yeni Ekle"
            />
          </ActionToolbar>

          {/* Filtrelele (İlişkili Tanımlar için) */}
          {(['subgroups', 'countries', 'cities'].includes(selectedDefinition.id)) && (
            <div className="flex items-center space-x-4 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedDefinition.id === 'subgroups' ? 'Ana Gruba' :
                  selectedDefinition.id === 'countries' ? 'Pazara' : 'Ülkeye'} Göre Filtrele:
              </label>
              <select
                value={selectedParentFilter}
                onChange={(e) => setSelectedParentFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">Tümü</option>
                {selectedDefinition.id === 'subgroups' && maingroups.map(mg => (
                  <option key={mg.id} value={mg.id}>{mg.maingroup_name}</option>
                ))}
                {selectedDefinition.id === 'countries' && markets.map(m => (
                  <option key={m.id} value={m.id}>{m.budget_market_name}</option>
                ))}
                {selectedDefinition.id === 'cities' && countries.map(c => (
                  <option key={c.id} value={c.id}>{c.country_name}</option>
                ))}
              </select>
              {selectedParentFilter && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredData.length} kayıt bulundu
                </span>
              )}
            </div>
          )}
        </div>

        {/* Tablo */}
        <div className="px-6 pb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                Yükleniyor...
              </div>
            ) : filteredData.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {selectedDefinition.id === 'subgroups' && selectedParentFilter
                  ? 'Bu ana gruba ait alt grup bulunamadı.'
                  : 'Henüz kayıt bulunmamaktadır.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        #
                      </th>
                      {selectedDefinition.fields.map(field => (
                        <th key={field.name} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          {field.label}
                        </th>
                      ))}
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredData.map((item, index) => (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {index + 1}
                        </td>
                        {selectedDefinition.fields.map(field => (
                          <td key={field.name} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {getDisplayValue(item, field)}
                          </td>
                        ))}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <IconActionButton
                              icon={PencilIcon}
                              onClick={() => handleEdit(item)}
                              title="Düzenle"
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            />
                            <IconActionButton
                              icon={TrashIcon}
                              onClick={() => handleDelete(item)}
                              title="Sil"
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {editingItem ? 'Düzenle' : 'Yeni Kayıt'}
              </h2>

              <div className="space-y-4">
                {selectedDefinition.fields.map(field => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {renderField(field)}
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  onClick={() => {
                    setIsModalOpen(false);
                    setFormData({});
                    setEditingItem(null);
                  }}
                  variant="outline"
                  className="px-4 py-2"
                >
                  İptal
                </Button>
                <Button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Kaydet
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingBudgetDefinitions;

